import { css, Theme } from '@emotion/react'
import { useLayoutEffect, useRef, useState } from 'react'

const containerStyle = {
  small: (t: Theme)=> css`
  position: relative;
  display: inline;
  margin-left: 4px;
  vertical-align: baseline;
`,
  medium: (t: Theme)=> css`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`,
}

const iconStyle = {
  small: (t: Theme)=> css`
  width: 14px;
  height: 14px;
  cursor: help;
  fill: #0084cf;
  vertical-align: -2px;
`,
  medium: (t: Theme)=> css`
  width: 20px;
  height: 20px;
  cursor: help;
  fill: #0084cf;
  vertical-align: -2px;
`,
}

const tooltipStyle = {
  small: (t: Theme)=> css `
  position: fixed;
  background-color: #333333;
  color: #ffffff;
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 12px;
  white-space: pre-line;
  z-index: 9999;
  pointer-events: none;

 --arrow-left: 50%;

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: var(--arrow-left);
    transform: translateX(-50%);
    border: 6px solid transparent;
    border-top-color: #333333;
  }
`,
  medium: (t: Theme)=> css `
  position: fixed;
  background-color: #333333;
  color: #ffffff;
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 12px;
  white-space: pre-line;
  max-width: 280px;
  z-index: 9999;
  pointer-events: none;

 --arrow-left: 50%;

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: var(--arrow-left);
    transform: translateX(-50%);
    border: 6px solid transparent;
    border-top-color: #333333;
  }
`,
}

type Props = { text: string, size: 'small' | 'medium' }
type Pos = { top: number; left: number }

const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v))

export const HelpIcon = ({ text, size }: Props) => {
  const [isVisible, setIsVisible] = useState(false)

  // anchor: 「本来矢印が指したい点（アイコン中心）」 ※viewport座標
  const [anchor, setAnchor] = useState<Pos>({ top: 0, left: 0 })

  // position: 「クランプ後の tooltip の基準座標」 ※viewport座標
  // ここでは transform: translate(-50%, -100%) なので
  // left は tooltip 中心X, top は tooltip 下端Y
  const [position, setPosition] = useState<Pos>({ top: 0, left: 0 })

  // arrowLeftPx: tooltip 左端から見た矢印の位置（px）
  const [arrowLeftPx, setArrowLeftPx] = useState<number | null>(null)

  const iconRef = useRef<SVGSVGElement>(null)
  const tooltipRef = useRef<HTMLSpanElement>(null)

  const GAP = 8
  const MARGIN = 8

  const handleMouseEnter = () => {
    const icon = iconRef.current
    if (!icon) return
    const rect = icon.getBoundingClientRect()

    setAnchor({
      top: rect.top - GAP,
      left: rect.left + rect.width / 2,
    })
    setIsVisible(true)
  }

  useLayoutEffect(() => {
    if (!isVisible) return
    const tip = tooltipRef.current
    if (!tip) return

    // まず「理想位置」で描画されている前提で、実寸を取得
    const tipRect = tip.getBoundingClientRect()
    const vw = window.innerWidth
    const vh = window.innerHeight

    const halfW = tipRect.width / 2

    // tooltip の中心Xをクランプ
    const clampedCenterX = clamp(anchor.left, MARGIN + halfW, vw - MARGIN - halfW)

    // tooltip の下端Yをクランプ（今回は上表示固定のまま）
    const clampedBottomY = clamp(anchor.top, MARGIN + tipRect.height, vh - MARGIN)

    setPosition({ left: clampedCenterX, top: clampedBottomY })

    // ---- 矢印位置の計算（重要） ----
    // tooltip 左端（viewport座標）を求める
    // transform: translate(-50%, -100%) なので left=中心X
    const tooltipLeftInViewport = clampedCenterX - tipRect.width / 2

    // アイコン中心(anchor.left)が、tooltip の左端から何pxの位置にあるか
    let arrow = anchor.left - tooltipLeftInViewport

    // 矢印が角丸やpaddingをはみ出さないように制限（任意だが推奨）
    // borderが6pxなので最低でも 6px + 1 くらい内側に寄せる
    const ARROW_HALF = 6
    arrow = clamp(arrow, MARGIN + ARROW_HALF, tipRect.width - (MARGIN + ARROW_HALF))

    setArrowLeftPx(arrow)
  }, [isVisible, anchor])

  return (
    <span
      css={containerStyle[size]}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => setIsVisible(false)}
    >
      <svg
        ref={iconRef}
        css={iconStyle[size]}
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 512 512"
      >
        <path d="M464 256A208 208 0 1 0 48 256a208 208 0 1 0 416 0zM0 256a256 256 0 1 1 512 0A256 256 0 1 1 0 256zm169.8-90.7c7.9-22.3 29.1-37.3 52.8-37.3l58.3 0c34.9 0 63.1 28.3 63.1 63.1c0 22.6-12.1 43.5-31.7 54.8L280 264.4c-.2 13-10.9 23.6-24 23.6c-13.3 0-24-10.7-24-24l0-13.5c0-8.6 4.6-16.5 12.1-20.8l44.3-25.4c4.7-2.7 7.6-7.7 7.6-13.1c0-8.4-6.8-15.1-15.1-15.1l-58.3 0c-3.4 0-6.4 2.1-7.5 5.3l-.4 1.2c-4.4 12.5-18.2 19-30.6 14.6s-19-18.2-14.6-30.6l.4-1.2zM224 352a32 32 0 1 1 64 0 32 32 0 1 1 -64 0z" />
      </svg>

      {isVisible && (
        <span
          ref={tooltipRef}
          css={tooltipStyle[size]}
          style={
            {
              top: position.top,
              left: position.left,
              transform: 'translate(-50%, -100%)',
              // CSS変数に px を渡して矢印の left を動かす
              ...(arrowLeftPx != null ? { ['--arrow-left' as any]: `${arrowLeftPx}px` } : {}),
            } as React.CSSProperties
          }
        >
          {text}
        </span>
      )}
    </span>
  )
}
